import React, { useState, useEffect } from "react";
import axios from "axios";
import { Navigation } from "../components/Navigation";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const ChiSiamoPage = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Navigation />

      {/* Hero scuro con sfondo edicola */}
      <section className="relative overflow-hidden" data-testid="chisiamo-hero">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/daba7cd3-bfb2-40c7-9ccb-537c58cf24ca/images/a46431d8d47f7fadf66c04c34771f25a895d25002a24f0ba0c84fa5e17e8bd4a.jpeg"
          alt="Edicola Cerasuolo"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#0a0a0f]"></div>
        <div className="relative max-w-7xl mx-auto px-4 py-24 text-center">
          <p className="text-pink-500 text-[11px] font-bold uppercase tracking-[0.3em] mb-3">Dal cuore di Villaricca</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase text-white mb-4">Chi <span className="text-pink-500">Siamo</span></h1>
          <p className="text-gray-300 text-base md:text-lg">La nostra storia e la nostra passione</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-14">
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-6">
            La nostra edicola è gestita con passione da <strong className="text-white">Cerasuolo Massimiliano</strong> e <strong className="text-white">Rita Colarusso</strong>,
            storica famiglia di edicolanti a Napoli con anni di esperienza nel settore.
          </p>
          <p className="text-gray-400 leading-relaxed">
            Ci siamo specializzati nel modellismo F1 delle migliori case editrici e nelle collezioni Pokémon,
            offrendo ai nostri clienti prodotti di qualità e un servizio personalizzato.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
          {[
            { icon: "🏎️", title: "Modellismo F1", desc: "Le grandi Ferrari, McLaren, Mercedes e tutte le scuderie storiche. Modelli in scala delle migliori case editrici." },
            { icon: "⚡", title: "Pokémon TCG", desc: "Mystery Box, carte rare, collezioni speciali e tutti i prodotti ufficiali del Trading Card Game." },
            { icon: "🌐", title: "Vendita Online", desc: "Trova i nostri prodotti anche su eBay e Wallapop per acquisti comodi da casa." },
          ].map((b) => (
            <div key={b.title} className="bg-[#12121a] border border-gray-800 hover:border-pink-500/50 rounded-xl p-7 text-center transition-colors duration-300">
              <div className="w-14 h-14 mx-auto mb-4 bg-pink-500/10 border border-pink-500/30 rounded-xl flex items-center justify-center text-2xl">{b.icon}</div>
              <h3 className="text-white font-extrabold uppercase text-base mb-3">{b.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center flex flex-wrap justify-center gap-3">
          <a href="/prodotti"
            className="inline-flex items-center px-8 py-3 bg-pink-600 hover:bg-pink-500 text-white font-extrabold uppercase text-xs tracking-wide skew-x-[-6deg] transition-colors"
            data-testid="chisiamo-cta-prodotti">
            <span className="skew-x-[6deg]">Scopri i Prodotti →</span>
          </a>
          <a href="/contatto"
            className="inline-flex items-center px-8 py-3 border border-gray-700 hover:border-pink-500/60 text-gray-300 hover:text-white font-extrabold uppercase text-xs tracking-wide skew-x-[-6deg] transition-colors"
            data-testid="chisiamo-cta-contatti">
            <span className="skew-x-[6deg]">Contattaci</span>
          </a>
        </div>
      </div>
    </div>
  );
};

// Contatto Page Component
export const ContattoPage = () => {
  const handleContactSubmit = (e) => {
    e.preventDefault();
    const f = e.target;
    const subject = encodeURIComponent(`Richiesta dal sito — ${f.nome.value}`);
    const body = encodeURIComponent(
      `Nome: ${f.nome.value}\nEmail: ${f.email.value}\nTelefono: ${f.telefono.value || "-"}\n\n${f.messaggio.value}`
    );
    window.location.href = `mailto:amm@edicolavillaricca.com?subject=${subject}&body=${body}`;
  };

  const inputClass = "mt-1 bg-[#0d0d14] border-gray-700 text-white placeholder-gray-500 focus-visible:ring-pink-500";

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Navigation />

      {/* Hero scuro */}
      <section className="relative overflow-hidden" data-testid="contatti-hero">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/daba7cd3-bfb2-40c7-9ccb-537c58cf24ca/images/0ba1492bbe4d4de8ab34bdc0d800dec09f7c11981a62d6db6cb84868f7b239a5.jpeg"
          alt="Contatta Edicola Cerasuolo"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#0a0a0f]"></div>
        <div className="relative max-w-7xl mx-auto px-4 py-24 text-center">
          <p className="text-pink-500 text-[11px] font-bold uppercase tracking-[0.3em] mb-3">Rispondiamo velocemente</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase text-white mb-4">Contatta<span className="text-pink-500">ci</span></h1>
          <p className="text-gray-300 text-base md:text-lg">Siamo qui per aiutarti</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Informazioni di contatto */}
          <div className="space-y-5">
            <div className="bg-[#12121a] border border-gray-800 rounded-xl p-6">
              <h3 className="text-white font-extrabold uppercase text-lg mb-3 flex items-center gap-2">📍 Dove Trovarci</h3>
              <p className="text-gray-400 mb-4">
                <strong className="text-white">Corso Europa 175</strong><br />
                80010 Villaricca (NA), Italia
              </p>
              <p className="text-gray-400 mb-4" data-testid="contatti-orari">
                🕒 <strong className="text-white">Orari negozio fisico:</strong> Lun-Sab 9:00-19:00 · Domenica chiuso
              </p>
              <div className="rounded-lg overflow-hidden border border-gray-800">
                <iframe
                  title="Mappa Edicola Cerasuolo — Corso Europa 175, Villaricca"
                  src="https://www.google.com/maps?q=Corso+Europa+175,+80010+Villaricca+NA,+Italia&output=embed"
                  className="w-full h-64 border-0 grayscale-[35%] contrast-105"
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contatti-map"
                ></iframe>
              </div>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=Corso+Europa+175,+80010+Villaricca+NA,+Italia"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-3 text-pink-400 hover:text-pink-300 text-sm font-semibold"
                data-testid="contatti-directions"
              >
                🧭 Indicazioni stradali →
              </a>
            </div>

            <div className="bg-[#12121a] border border-gray-800 rounded-xl p-6">
              <h3 className="text-white font-extrabold uppercase text-lg mb-3 flex items-center gap-2">📞 Contatti Diretti</h3>
              <p className="text-gray-400 mb-2">
                <strong className="text-white">Telefono:</strong>
                <a href="tel:+393470808245" className="text-pink-400 hover:text-pink-300 ml-2" data-testid="contatti-phone">
                  +39 347 080 8245
                </a>
              </p>
              <p className="text-gray-400 mb-4">
                <strong className="text-white">Email:</strong>
                <a href="mailto:amm@edicolavillaricca.com" className="text-pink-400 hover:text-pink-300 ml-2" data-testid="contatti-email">
                  amm@edicolavillaricca.com
                </a>
              </p>
              <a href="https://wa.me/393470808245?text=Ciao!%20Vi%20contatto%20dal%20sito" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-2.5 bg-[#25D366] hover:bg-white text-black font-extrabold uppercase text-xs tracking-wide skew-x-[-6deg] transition-colors"
                data-testid="contatti-whatsapp-cta">
                <span className="skew-x-[6deg]">💬 Scrivici su WhatsApp →</span>
              </a>
            </div>

            <div className="bg-[#12121a] border border-gray-800 rounded-xl p-6">
              <h3 className="text-white font-extrabold uppercase text-lg mb-3 flex items-center gap-2">🌐 Vendita Online</h3>
              <div className="space-y-3">
                <a
                  href="https://ebay.it/str/edicolacerasuolo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-gray-400 hover:text-yellow-300 transition-colors"
                  data-testid="contatti-ebay-link"
                >
                  <span className="text-yellow-400 font-extrabold uppercase mr-2">eBay</span> I nostri prodotti su eBay →
                </a>
                <a
                  href="https://it.wallapop.com/app/catalog/published"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-gray-400 hover:text-[#13C1AC] transition-colors"
                  data-testid="contatti-wallapop-link"
                >
                  <span className="text-[#13C1AC] font-extrabold uppercase mr-2">Wallapop</span> I nostri prodotti su Wallapop →
                </a>
              </div>
            </div>
          </div>

          {/* Form di contatto */}
          <div className="bg-[#12121a] border border-gray-800 rounded-xl p-8 h-fit">
            <h3 className="text-white font-extrabold uppercase text-lg mb-6 flex items-center gap-2">
              <span className="inline-block w-8 h-[3px] bg-pink-500"></span> Invia un Messaggio
            </h3>
            <form className="space-y-5" onSubmit={handleContactSubmit} data-testid="contatti-form">
              <div>
                <Label htmlFor="nome" className="text-gray-300">Nome *</Label>
                <Input id="nome" name="nome" type="text" required className={inputClass} data-testid="contatti-form-nome" />
              </div>
              <div>
                <Label htmlFor="email" className="text-gray-300">Email *</Label>
                <Input id="email" name="email" type="email" required className={inputClass} data-testid="contatti-form-email" />
              </div>
              <div>
                <Label htmlFor="telefono" className="text-gray-300">Telefono</Label>
                <Input id="telefono" name="telefono" type="tel" className={inputClass} data-testid="contatti-form-telefono" />
              </div>
              <div>
                <Label htmlFor="messaggio" className="text-gray-300">Messaggio *</Label>
                <Textarea id="messaggio" name="messaggio" rows={5} required className={inputClass} data-testid="contatti-form-messaggio" />
              </div>
              <Button type="submit" className="w-full bg-pink-600 hover:bg-pink-500 text-white font-extrabold uppercase tracking-wide" data-testid="contatti-form-submit">
                Invia Messaggio
              </Button>
              <p className="text-gray-600 text-[11px] text-center">Il messaggio verrà inviato tramite il tuo client email a amm@edicolavillaricca.com</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

// Norme sui Resi e Diritto di Recesso (richiesto da Google Merchant Center)
export const ResiPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">↩️ Norme sui Resi e Diritto di Recesso</h1>
          <p className="text-sm text-gray-600 mb-8">Ultima modifica: 22 Giugno 2026</p>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
            <p className="text-amber-900 text-sm">
              <strong>In breve:</strong> hai <strong>14 giorni</strong> dalla ricezione per restituire un prodotto integro nell&apos;imballo originale.
              Le <strong>spese di reso sono a carico del cliente</strong>. Il rimborso arriva entro <strong>14 giorni</strong> dalla ricezione del reso, con la stessa modalità di pagamento usata per l&apos;acquisto.
            </p>
          </div>

          <div className="space-y-8 text-gray-700 leading-relaxed">

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">1. Periodo per esercitare il reso</h2>
              <p>
                Ai sensi degli articoli 52 e seguenti del D.Lgs. 206/2005 (Codice del Consumo), hai diritto di recedere dal contratto entro <strong>14 giorni di calendario</strong> dalla data di ricezione fisica del prodotto, senza dover fornire alcuna motivazione.
              </p>
              <p className="mt-2">
                Il termine si considera rispettato se la comunicazione di recesso viene inviata prima della scadenza dei 14 giorni.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">2. Condizioni del prodotto reso</h2>
              <p>Per essere accettato, il prodotto deve essere restituito:</p>
              <ul className="list-disc list-inside space-y-1 mt-2 ml-2">
                <li>Integro, non danneggiato e non usato oltre quanto necessario per verificarne natura e caratteristiche</li>
                <li>Nella confezione originale, comprensiva di tutti gli accessori, manuali, etichette ed eventuali sigilli</li>
                <li>Privo di segni evidenti di utilizzo (graffi, tracce, residui, ecc.)</li>
                <li>Sigilli non rimossi se trattasi di articoli sigillati (carte da gioco, modellini sigillati ecc.)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">3. Come avviare un reso</h2>
              <ol className="list-decimal list-inside space-y-2 ml-2">
                <li>
                  Contattaci entro 14 giorni dalla ricezione tramite uno dei seguenti canali:
                  <div className="ml-6 mt-2 p-3 bg-gray-50 rounded">
                    <p>📧 Email: <a href="mailto:amm@edicolavillaricca.com" className="text-blue-600 hover:underline">amm@edicolavillaricca.com</a></p>
                    <p>📞 Telefono / WhatsApp: <a href="tel:+393470808245" className="text-blue-600 hover:underline">+39 347 080 8245</a></p>
                  </div>
                </li>
                <li>Indica numero d&apos;ordine, prodotto/i da restituire e motivo del reso.</li>
                <li>Riceverai conferma scritta e l&apos;indirizzo a cui inviare il pacco.</li>
                <li>Spedisci il pacco entro 14 giorni dalla nostra conferma, conservando la ricevuta di spedizione.</li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">4. Chi paga le spese di spedizione del reso</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
                  <h3 className="font-semibold text-red-800 mb-2">Reso volontario (cambio idea)</h3>
                  <p className="text-sm">Spese di spedizione di ritorno <strong>a carico del cliente</strong>.</p>
                </div>
                <div className="p-4 border border-green-200 bg-green-50 rounded-lg">
                  <h3 className="font-semibold text-green-800 mb-2">Prodotto difettoso, errato o danneggiato</h3>
                  <p className="text-sm">Spese di reso <strong>completamente a nostro carico</strong>.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">5. Metodo e tempi di rimborso</h2>
              <p>
                Una volta ricevuto il prodotto e verificata la sua integrità, procediamo al rimborso entro <strong>14 giorni</strong> dalla ricezione del reso.
              </p>
              <p className="mt-2">
                Il rimborso avviene <strong>con la stessa modalità di pagamento</strong> utilizzata per l&apos;acquisto:
              </p>
              <ul className="list-disc list-inside space-y-1 mt-2 ml-2">
                <li>Pagamento con carta → riaccredito sulla carta originale</li>
                <li>Stripe / Klarna / Satispay → rimborso sul circuito originale</li>
                <li>Bonifico bancario → bonifico sul medesimo IBAN del versamento (o IBAN che ci comunicherai)</li>
              </ul>
              <p className="mt-2 text-sm text-gray-600">
                I tempi di accredito effettivo possono variare in funzione della banca o circuito di pagamento (di norma 3-7 giorni lavorativi aggiuntivi).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">6. Esclusioni dal diritto di recesso</h2>
              <p>Ai sensi dell&apos;art. 59 del Codice del Consumo, sono <strong>esclusi dal diritto di recesso</strong>:</p>
              <ul className="list-disc list-inside space-y-1 mt-2 ml-2">
                <li>Prodotti confezionati su misura o chiaramente personalizzati</li>
                <li>Prodotti sigillati che siano stati aperti dopo la consegna (es. carte collezionabili sigillate, modellini in confezione sigillata aperta)</li>
                <li>Riviste e periodici (giornali, fumetti, riviste)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">7. Sostituzione invece del rimborso</h2>
              <p>
                Se preferisci una sostituzione con un altro prodotto di pari valore (o pagando/ricevendo la differenza), comunicalo al momento della richiesta di reso. Saremo felici di aiutarti a trovare l&apos;articolo giusto.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">8. Contatti per i resi</h2>
              <div className="p-4 bg-blue-50 rounded-lg">
                <p><strong>Edicola Cerasuolo</strong></p>
                <p>📍 Corso Europa 175, 80010 Villaricca (NA)</p>
                <p>📞 +39 347 080 8245</p>
                <p>📧 <a href="mailto:amm@edicolavillaricca.com" className="text-blue-600 hover:underline">amm@edicolavillaricca.com</a></p>
                <p className="text-sm text-gray-600 mt-2">Orari negozio fisico: Lun-Sab 9:00 - 19:00 | Domenica chiuso</p>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

// Termini di Vendita Page
export const TerminiPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">📋 Termini e Condizioni di Vendita</h1>
          <p className="text-sm text-gray-600 mb-8">Ultima modifica: 15 Settembre 2024</p>

          <div className="space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">1. Informazioni Generali</h2>
              <p className="text-gray-700 leading-relaxed">
                <strong>Edicola Cerasuolo</strong> è un'edicola specializzata in modellismo Formula 1 e carte Pokémon,
                gestita con passione da Massimiliano Cerasuolo e Rita Colarusso.
              </p>
              <div className="mt-3 p-4 bg-blue-50 rounded-lg">
                <p><strong>📍 Indirizzo:</strong> Corso Europa 175, Villaricca (NA)</p>
                <p><strong>📞 Telefono:</strong> +39 347 080 8245</p>
                <p><strong>📧 Email:</strong> amm@edicolavillaricca.com</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">2. Modalità di Pagamento</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold flex items-center mb-2">🏦 Bonifico Bancario</h3>
                  <p className="text-sm text-gray-600">IBAN: IE67SUMU99036510351260<br/>Intestato a: Cerasuolo Massimiliano</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold flex items-center mb-2">💳 Carta di Credito</h3>
                  <p className="text-sm text-gray-600">Pagamento sicuro tramite Stripe con crittografia SSL</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold flex items-center mb-2">🔷 Klarna</h3>
                  <p className="text-sm text-gray-600">Paga dopo 30 giorni o in 3 rate</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold flex items-center mb-2">🟥 Satispay</h3>
                  <p className="text-sm text-gray-600">Pagamento mobile veloce e sicuro tramite app Satispay</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">3. Spedizioni e Consegne</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Spedizioni nazionali:</strong> Tempi di consegna 2-3 giorni lavorativi</li>
                <li><strong>Spedizioni internazionali:</strong> Disponibili su richiesta, tempi variabili</li>
                <li><strong>Costi di spedizione:</strong> Calcolati in base al peso e alla destinazione</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">4. Diritto di Recesso</h2>
              <p className="text-gray-700 leading-relaxed">
                In conformità al Codice del Consumo, hai diritto di recedere dal contratto entro 14 giorni 
                dalla ricezione della merce, senza dover specificare il motivo. Per esercitare il diritto di recesso, 
                contattaci al +39 347 080 8245 o via email a amm@edicolavillaricca.com.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">5. Garanzie e Resi</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Tutti i prodotti sono coperti da garanzia legale di conformità</li>
                <li>I prodotti devono essere restituiti nelle stesse condizioni di ricezione</li>
                <li>Le spese di reso sono a carico del cliente, salvo difetti del prodotto</li>
                <li>Il rimborso avviene entro 14 giorni dalla ricezione del reso</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">6. Privacy e Newsletter</h2>
              <p className="text-gray-700 leading-relaxed">
                I tuoi dati personali sono trattati in conformità al GDPR. Iscrivendoti alla newsletter, 
                acconsenti a ricevere comunicazioni sui nostri prodotti e servizi. Puoi cancellarti in qualsiasi momento 
                utilizzando il link nelle email o contattandoci direttamente.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">7. Contatti e Supporto</h2>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-gray-700">
                  Per qualsiasi domanda, problema o chiarimento sui nostri termini di vendita, 
                  non esitare a contattarci:
                </p>
                <div className="mt-3">
                  <p>📞 <strong>Telefono:</strong> +39 347 080 8245</p>
                  <p>📧 <strong>Email:</strong> amm@edicolavillaricca.com</p>
                  <p>🕒 <strong>Orari negozio fisico:</strong> Lun-Sab 9:00-19:00 | Domenica chiuso</p>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t">
            <p className="text-center text-sm text-gray-500">
              © 2024 Edicola Cerasuolo - Tutti i diritti riservati
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Newsletter Unsubscribe Page
export const NewsletterUnsubscribePage = () => {
  const [token, setToken] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Get token from URL if present
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    }
  }, []);

  const handleUnsubscribe = async (e) => {
    e.preventDefault();
    
    if (!token.trim()) {
      setMessage("Inserisci un token di cancellazione valido");
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      const response = await axios.post(`${API}/newsletter/unsubscribe`, {
        token: token.trim()
      });

      setMessage("✅ Cancellazione completata con successo! Non riceverai più email dalla nostra newsletter.");
      setToken("");

    } catch (error) {
      console.error("Unsubscribe error:", error);
      if (error.response?.status === 404) {
        setMessage("❌ Token non valido o già utilizzato. Contattaci per assistenza.");
      } else {
        setMessage("❌ Errore nella cancellazione. Riprova più tardi o contattaci direttamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO: never index the unsubscribe page (functional, not informative) */}
      <meta name="robots" content="noindex, nofollow" />
      <link rel="canonical" href="https://edicolavillaricca.com/" />
      <Navigation />
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">📧 Cancellazione Newsletter</h1>
          
          <div className="text-center mb-8">
            <p className="text-gray-600">
              Ci dispiace vederti andare! Se vuoi cancellarti dalla nostra newsletter, 
              inserisci il token di cancellazione che hai ricevuto via email.
            </p>
          </div>

          <form onSubmit={handleUnsubscribe} className="space-y-6">
            <div>
              <label htmlFor="token" className="block text-sm font-medium text-gray-700 mb-2">
                Token di Cancellazione
              </label>
              <Input
                id="token"
                type="text"
                placeholder="Inserisci il token ricevuto via email"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="w-full"
                required
              />
              <p className="text-xs text-gray-500 mt-2">
                Il token si trova nell'email di conferma che hai ricevuto quando ti sei iscritto.
              </p>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Elaborazione...
                </>
              ) : (
                "Cancella Iscrizione"
              )}
            </Button>

            {message && (
              <div className={`p-4 rounded-lg text-sm ${
                message.includes("✅") 
                  ? "bg-green-100 text-green-800 border border-green-200" 
                  : "bg-red-100 text-red-800 border border-red-200"
              }`}>
                {message}
              </div>
            )}
          </form>

          <div className="mt-8 pt-6 border-t text-center">
            <p className="text-gray-600 mb-4">
              Hai problemi con la cancellazione? Contattaci direttamente:
            </p>
            <div className="space-y-2">
              <p>📞 <strong>+39 347 080 8245</strong></p>
              <p>📧 <strong>amm@edicolavillaricca.com</strong></p>
            </div>
            
            <div className="mt-6">
              <a href="/" className="text-blue-600 hover:underline">
                ← Torna alla Homepage
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Stripe Success Page Component
export const StripeSuccessPage = () => {
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);

  useEffect(() => {
    const processStripeSuccess = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const isPayPal = urlParams.get('paypal') === '1';
        // PayPal returns the order id in `token`; Stripe uses `session_id`.
        const sessionId = urlParams.get('session_id') || urlParams.get('token');

        if (!sessionId) {
          setStatus('error');
          setMessage('ID sessione mancante. Contatta il supporto.');
          return;
        }

        // ─── PayPal branch: capture funds server-side, then poll status ──
        if (isPayPal || sessionId.length === 17) {
          try {
            await axios.post(`${API}/paypal/capture-order/${sessionId}`);
          } catch (capErr) {
            // If capture fails but transaction was already paid, keep going.
            console.warn("PayPal capture returned error (may be idempotent):", capErr);
          }
          const response = await axios.get(`${API}/paypal/status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            setStatus('success');
            setOrderDetails(response.data);
            setMessage('🎉 Pagamento PayPal completato con successo!');
            localStorage.removeItem('cart');
            localStorage.removeItem('customerInfo');
          } else {
            setStatus('error');
            setMessage(`Pagamento PayPal non completato (${response.data.payment_status}). Contatta il supporto.`);
          }
          return;
        }

        // ─── Stripe branch (unchanged) ───────────────────────────────────
        const statusUrl = sessionId.startsWith("pi_")
          ? `${API}/express-checkout/status/${sessionId}`
          : `${API}/checkout/status/${sessionId}`;
        const response = await axios.get(statusUrl);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setOrderDetails(response.data);
          setMessage('🎉 Pagamento completato con successo!');
          // ─── GA4 Ecommerce purchase event ───────────────────────────────
          // Fires only once per session_id (localStorage guard) so browser
          // refresh doesn't double-count. Requires marketing consent.
          try {
            const alreadySent = localStorage.getItem(`ga4_purchase_sent_${sessionId}`);
            const hasConsent = typeof window.hasCookieConsent === 'function'
              ? window.hasCookieConsent('analytics')
              : true;
            if (!alreadySent && hasConsent && typeof window.gtag === 'function') {
              const amountEuro = (response.data.amount_total || 0) / 100;
              const items = (response.data.items || []).map((it, idx) => ({
                item_id: it.article_id || `item_${idx}`,
                item_name: it.title || 'Prodotto',
                price: Number(it.price) || 0,
                quantity: Number(it.quantity) || 1,
              }));
              window.gtag('event', 'purchase', {
                transaction_id: sessionId,
                value: amountEuro,
                currency: response.data.currency ? response.data.currency.toUpperCase() : 'EUR',
                tax: 0,
                shipping: Number(response.data.shipping_cost) || 0,
                coupon: response.data.coupon || undefined,
                items,
              });
              localStorage.setItem(`ga4_purchase_sent_${sessionId}`, '1');
              console.log('📊 GA4 purchase event sent:', { transaction_id: sessionId, value: amountEuro, items_count: items.length });
            }
          } catch (e) {
            console.warn('GA4 purchase tracking failed (non-blocking):', e);
          }

          // Clear cart and customer info after successful payment
          localStorage.removeItem('cart');
          localStorage.removeItem('customerInfo');
        } else {
          setStatus('pending');
          setMessage('⏳ Pagamento in elaborazione...');
        }

      } catch (error) {
        console.error('Error processing Stripe success:', error);
        setStatus('error');
        setMessage('Errore nel verificare il pagamento. Contatta il supporto.');
      }
    };

    processStripeSuccess();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="text-center text-2xl">
            {status === 'loading' && '⏳ Verifica in corso...'}
            {status === 'success' && '✅ Ordine Confermato!'}
            {status === 'pending' && '⏳ In Elaborazione'}
            {status === 'error' && '❌ Errore'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'success' && orderDetails?.order_id && (
            <a
              href={`https://t.me/Edicolavillariccabot?start=o_${orderDetails.order_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center bg-[#229ED9] hover:bg-[#1c86b8] text-white font-bold py-3 rounded-lg"
              data-testid="stripe-success-telegram-btn"
            >
              🔔 Ricevi aggiornamenti su Telegram
            </a>
          )}
          <div className="flex flex-col gap-2">
            <Button 
              onClick={() => window.location.href = '/prodotti'}
              className="w-full"
            >
              🛍️ Continua gli acquisti
            </Button>
            <Button 
              onClick={() => window.location.href = '/'}
              variant="outline"
              className="w-full"
            >
              🏠 Torna alla Homepage
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// PayPalSuccessPage was removed on 28 May 2026 when the merchant account
// was closed. The /paypal/success route is no longer mounted.

// Satispay Success Page — landing after the customer completes the QR/redirect
// payment on online.satispay.com. The Satispay webhook (server-to-server) has
// likely already marked the order as paid, but this page also polls the order
// status briefly to give the user instant feedback when they return.
export const SatispaySuccessPage = () => {
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("⏳ Verifica pagamento Satispay in corso...");

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const externalCode = urlParams.get("external_code") ||
                          (typeof localStorage !== "undefined" && localStorage.getItem("pendingSatispayOrderId"));

    if (!externalCode) {
      setStatus("error");
      setMessage("Riferimento ordine mancante. Se il pagamento è andato a buon fine, riceverai conferma via email.");
      return;
    }

    // Poll Satispay payment status for up to 30s — the webhook is usually
    // faster but on slow networks we want to display the right state.
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const resp = await axios.get(`${API}/satispay/payment-status/${encodeURIComponent(externalCode)}`);
        if (cancelled) return;
        const st = resp.data?.status;
        if (st === "ACCEPTED" || st === "PAID") {
          setStatus("success");
          setMessage("🎉 Pagamento completato con successo!");
          try {
            localStorage.removeItem("cart");
            localStorage.removeItem("customerInfo");
            localStorage.removeItem("pendingSatispayOrderId");
          } catch (_) { /* ignore */ }
          return;
        }
        if (st === "CANCELED" || st === "EXPIRED") {
          setStatus("canceled");
          setMessage("❌ Il pagamento è stato annullato. Puoi riprovare dal carrello.");
          return;
        }
      } catch (_) { /* keep polling */ }
      if (attempts < 10) setTimeout(poll, 3000);
      else {
        setStatus("pending");
        setMessage("⏳ Pagamento in elaborazione. Riceverai una email di conferma a breve.");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-orange-50 flex items-center justify-center p-4" data-testid="satispay-success-page">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="text-center text-2xl">
            {status === "loading" && "⏳ Verifica Satispay..."}
            {status === "success" && "✅ Pagamento Confermato!"}
            {status === "pending" && "⏳ In Elaborazione"}
            {status === "canceled" && "❌ Pagamento Annullato"}
            {status === "error" && "⚠️ Attenzione"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-gray-700 whitespace-pre-line">{message}</p>
          {status === "success" && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-gray-700 text-center">
              📧 Riceverai una email con tutti i dettagli dell'ordine.<br />
              📦 Spediremo entro 24-48h lavorative.
            </div>
          )}
          <div className="flex flex-col gap-2">
            <Button onClick={() => window.location.href = "/prodotti"} className="w-full" data-testid="satispay-success-continue-shopping">
              🛍️ Continua gli acquisti
            </Button>
            <Button onClick={() => window.location.href = "/"} variant="outline" className="w-full">
              🏠 Torna alla Homepage
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Privacy Policy Page Component

